import type { ValidationEngineRule, ValidationResult, ValidationContext, ChannelErrors } from './types';
import type { MessageResolveFn } from './messageResolver';
import { normalizeMessages } from './messageResolver';
import { doesRuleApplyToFlow } from './flow';

function isConditionMet(rule: ValidationEngineRule, ctx: ValidationContext, value: unknown, scope: unknown): boolean {
    const conditions = rule.conditions;
    if (!conditions) return true;

    const state = ctx.state ?? ctx.buyerState;
    const offerType = ctx.offerType;
    const fuelType = ctx.fuelType;
    const entity = ctx.entity;

    if (conditions.applicableEntities?.length) {
        if (!entity || !conditions.applicableEntities.includes(entity)) return false;
    }

    if (conditions.applicableStates?.length) {
        if (!state || !conditions.applicableStates.includes(state)) return false;
    }

    if (conditions.excludeStates?.length) {
        if (state && conditions.excludeStates.includes(state)) return false;
    }

    if (conditions.includeOfferTypes?.length) {
        if (!offerType || !conditions.includeOfferTypes.includes(offerType)) return false;
    }

    if (conditions.excludeOfferTypes?.length) {
        if (offerType && conditions.excludeOfferTypes.includes(offerType)) return false;
    }

    if (conditions.excludeFuelTypes?.length) {
        if (fuelType && conditions.excludeFuelTypes.includes(fuelType)) return false;
    }

    if (conditions.excludeDealModes?.length) {
        const dealMode = ctx.dealMode;
        if (dealMode && conditions.excludeDealModes.includes(dealMode)) return false;
    }

    if (conditions.enabledToggles != null) {
        const toggles = Array.isArray(conditions.enabledToggles) ? conditions.enabledToggles : [conditions.enabledToggles];
        const ft = ctx.featureToggles;
        if (toggles.some((t) => !ft?.[t])) return false;
    }

    if (conditions.disabledToggles != null) {
        const toggles = Array.isArray(conditions.disabledToggles) ? conditions.disabledToggles : [conditions.disabledToggles];
        const ft = ctx.featureToggles;
        if (toggles.some((t) => ft?.[t] === true)) return false;
    }

    if (typeof conditions.when === 'function') {
        try {
            if (!conditions.when(ctx, value, scope)) return false;
        } catch {
            return false;
        }
    }

    return true;
}

export interface ValidateOptions {
    optional?: boolean;
    ctx?: Partial<ValidationContext>;
    messageResolver?: MessageResolveFn;
    scope?: unknown;
    // When true, evaluates all rules and aggregates messages. Default shortcircuits on first violation.
    runAllRules?: boolean;
}

export function validateValue(rules: ValidationEngineRule[], value: any, options?: ValidateOptions): ValidationResult {
    const channels: ChannelErrors = {};
    let valid = true;
    const optional = options?.optional ?? false;
    // Merge full engine context, preserving custom keys and applying sensible defaults
    const mergedCtx: any = { ...(options?.ctx ?? {}) };
    mergedCtx.values = mergedCtx.values ?? {};
    mergedCtx.path = mergedCtx.path ?? '';
    mergedCtx.flow = mergedCtx.flow ?? 'default';
    mergedCtx.uiMode = mergedCtx.uiMode ?? 'deal-screen';
    // Leave optional, stateful keys as-is; they may be undefined by design
    const ctx: ValidationContext = mergedCtx as ValidationContext;

    const v = value;
    const isEmpty = v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

    const scope = options?.scope;

    const runAll = options?.runAllRules === true;
    for (const rule of rules) {
        if (!doesRuleApplyToFlow(rule.flow, ctx.flow)) {
            continue;
        }

        // Condition gating: skip rules that aren't applicable for the current context
        if (!isConditionMet(rule, ctx, v, scope)) {
            continue;
        }

        let violated = false;
        // If value is missing/empty, only REQUIRED should fail; skip other rules
        if (isEmpty && rule.type !== 'REQUIRED') {
            continue;
        }
        switch (rule.type) {
            case 'REQUIRED':
                violated = !optional && isEmpty;
                break;
            case 'MIN_LENGTH': {
                const min = (rule.params as any)?.min ?? (rule.params as any)?.length;
                violated = typeof v === 'string' && typeof min === 'number' ? v.length < min : false;
                break;
            }
            case 'MAX_LENGTH': {
                const max = (rule.params as any)?.max ?? (rule.params as any)?.length;
                violated = typeof v === 'string' && typeof max === 'number' ? v.length > max : false;
                break;
            }
            case 'REGEX': {
                const pattern = (rule.params as any)?.regex ?? (rule.params as any)?.pattern;
                const re = pattern instanceof RegExp ? pattern : typeof pattern === 'string' ? new RegExp(pattern) : undefined;
                violated = re ? !re.test(String(v)) : false;
                break;
            }
            case 'EQUALS': {
                const eq = (rule.params as any)?.equals;
                violated = eq !== undefined ? v !== eq : false;
                break;
            }
            case 'CUSTOM': {
                const evaluator = (rule.params as any)?.evaluator;
                if (typeof evaluator === 'function') {
                    const ok = evaluator(v, ctx);
                    violated = ok === true ? false : true;
                } else {
                    violated = false;
                }
                break;
            }
            default:
                violated = false;
        }

        if (violated) {
            valid = false;
            const resolved = normalizeMessages(rule.messages, ctx, options?.messageResolver);
            const inlineMsg = resolved.inline ?? (rule.type === 'REQUIRED' ? 'Required' : undefined);
            const gcMsg = resolved.gcalert ?? inlineMsg;
            if (inlineMsg) channels.inline = [...(channels.inline ?? []), inlineMsg];
            if (gcMsg) channels.gcalert = [...(channels.gcalert ?? []), gcMsg];
            if (!runAll) {
                break;
            }
        }
    }

    return { valid, channels };
}
